import React, { useState, useEffect } from 'react';
import { 
  CheckCircle, 
  Circle, 
  AlertCircle, 
  Loader2,
  ChevronRight,
  Copy,
  Eye,
  EyeOff,
  Terminal,
  FileText,
  Clock,
  Hash,
  User,
  Key,
  Phone,
  MessageSquare,
  Send,
  Shield,
  Database,
  Server,
  Globe,
  Zap,
  CheckSquare,
  Square,
  ArrowRight,
  RotateCcw,
  RefreshCw
} from 'lucide-react';

interface Step {
  id: string;
  letter: string;
  title: string;
  description: string;
  command?: string;
  input?: string;
  expectedOutput?: string;
  actualOutput?: string;
  status: 'pending' | 'in-progress' | 'completed' | 'failed' | 'skipped';
  startTime?: Date;
  endTime?: Date;
  duration?: number;
  notes?: string;
  verification?: {
    method: string;
    endpoint?: string;
    expected?: any;
    actual?: any;
  };
}

interface AuditEntry {
  timestamp: Date;
  step: string;
  action: string;
  result: string;
  duration: number;
  details?: any;
}

const STORAGE_KEY = 'methodical-setup-state';
const INITIAL_STEPS: Step[] = [
    {
      id: 'a',
      letter: 'a',
      title: 'Verify Project Directory',
      description: 'Confirm we are in the correct directory',
      command: 'pwd',
      expectedOutput: '/Users/brianball/Code/biig-biz',
      status: 'pending'
    },
    {
      id: 'b',
      letter: 'b',
      title: 'Check Git Status',
      description: 'Ensure we have a clean working directory',
      command: 'git status',
      expectedOutput: 'Your branch is up to date',
      status: 'pending'
    },
    {
      id: 'c',
      letter: 'c',
      title: 'Check Git Remotes',
      description: 'Display all configured git remotes',
      command: 'git remote -v',
      expectedOutput: 'origin (fetch/push)',
      status: 'pending'
    },
    {
      id: 'd',
      letter: 'd',
      title: 'Navigate to API Directory',
      description: 'Change to the API directory where our worker lives',
      command: 'cd apps/api',
      expectedOutput: 'Changed directory successfully',
      status: 'pending'
    },
    {
      id: 'e',
      letter: 'e',
      title: 'Verify Worker Name',
      description: 'Confirm the worker name is biig-api',
      command: 'grep "name" wrangler.toml | head -1',
      expectedOutput: 'name = "biig-api"',
      status: 'pending'
    },
    {
      id: 'f',
      letter: 'f',
      title: 'List Current Secrets',
      description: 'Check what secrets already exist',
      command: 'npx wrangler secret list',
      expectedOutput: 'List of secrets',
      status: 'pending'
    },
    {
      id: 'g',
      letter: 'g',
      title: 'Open Twilio Console',
      description: 'Navigate to console.twilio.com in your browser',
      command: 'open https://console.twilio.com',
      expectedOutput: 'Browser opened',
      status: 'pending'
    },
    {
      id: 'h',
      letter: 'h',
      title: 'Locate Account SID',
      description: 'Find your Account SID on the Twilio dashboard (starts with AC)',
      input: 'Account SID',
      expectedOutput: 'AC[32 characters]',
      status: 'pending'
    },
    {
      id: 'i',
      letter: 'i',
      title: 'Copy Account SID',
      description: 'Copy the Account SID to your clipboard',
      command: 'echo "AC..." | pbcopy',
      expectedOutput: 'Copied to clipboard',
      status: 'pending'
    },
    {
      id: 'j',
      letter: 'j',
      title: 'Set TWILIO_ACCOUNT_SID',
      description: 'Add Account SID as a secret to the worker',
      command: 'npx wrangler secret put TWILIO_ACCOUNT_SID',
      expectedOutput: '✨ Success! Uploaded secret TWILIO_ACCOUNT_SID',
      status: 'pending'
    },
    {
      id: 'k',
      letter: 'k',
      title: 'Verify Account SID is Set',
      description: 'Confirm the secret was added',
      command: 'npx wrangler secret list | grep TWILIO_ACCOUNT_SID',
      expectedOutput: 'TWILIO_ACCOUNT_SID',
      status: 'pending'
    },
    {
      id: 'l',
      letter: 'l',
      title: 'Locate Auth Token',
      description: 'Find your Auth Token on the Twilio dashboard',
      input: 'Auth Token',
      expectedOutput: '[32 character token]',
      status: 'pending'
    },
    {
      id: 'm',
      letter: 'm',
      title: 'Set TWILIO_AUTH_TOKEN',
      description: 'Add Auth Token as a secret to the worker',
      command: 'npx wrangler secret put TWILIO_AUTH_TOKEN',
      expectedOutput: '✨ Success! Uploaded secret TWILIO_AUTH_TOKEN',
      status: 'pending'
    },
    {
      id: 'n',
      letter: 'n',
      title: 'Navigate to WhatsApp Sandbox',
      description: 'Go to Messaging > Try it out > Send a WhatsApp message',
      command: 'open https://console.twilio.com/us1/develop/sms/try-it-out/whatsapp-learn',
      expectedOutput: 'Twilio WhatsApp Sandbox page opened',
      status: 'pending'
    },
    {
      id: 'n2',
      letter: 'n2',
      title: 'Retrieve Your Sandbox Code',
      description: 'Use curl to get your sandbox join code from Twilio (requires being logged in)',
      command: 'echo "Please manually check the Twilio Console for your sandbox code"',
      expectedOutput: 'Manual check required - look for "join [word-word]" on the page',
      status: 'pending'
    },
    {
      id: 'o',
      letter: 'o',
      title: 'Record Your Sandbox Keyword',
      description: 'Write down YOUR unique sandbox code from the Twilio page',
      input: 'Your sandbox join code (e.g., "join arctic-zebra")',
      expectedOutput: 'Sandbox code recorded for next steps',
      status: 'pending'
    },
    {
      id: 'p',
      letter: 'p',
      title: 'Open WhatsApp on Phone',
      description: 'Open WhatsApp application on your mobile device',
      expectedOutput: 'WhatsApp opened',
      status: 'pending'
    },
    {
      id: 'q',
      letter: 'q',
      title: 'Add Twilio Contact',
      description: 'Add +1 415 523 8886 as a new contact',
      input: 'Contact Name',
      expectedOutput: 'Twilio Sandbox',
      status: 'pending'
    },
    {
      id: 'r',
      letter: 'r',
      title: 'Send Join Message',
      description: 'Send your sandbox keyword to the Twilio number',
      command: 'Message: "join [your-keyword]"',
      expectedOutput: 'Twilio Sandbox: ✅ You are all set!',
      status: 'pending'
    },
    {
      id: 's',
      letter: 's',
      title: 'Set TWILIO_WHATSAPP_NUMBER',
      description: 'Add the WhatsApp sandbox number',
      command: 'npx wrangler secret put TWILIO_WHATSAPP_NUMBER',
      input: 'whatsapp:+14155238886',
      expectedOutput: '✨ Success! Uploaded secret TWILIO_WHATSAPP_NUMBER',
      status: 'pending'
    },
    {
      id: 't',
      letter: 't',
      title: 'Deploy API Changes',
      description: 'Deploy the API with all secrets configured',
      command: 'npm run deploy',
      expectedOutput: 'Deployed biig-api triggers',
      status: 'pending'
    },
    {
      id: 'u',
      letter: 'u',
      title: 'Test API Health',
      description: 'Verify the API is responding',
      command: 'curl https://biig-api.b-9f2.workers.dev/api/health',
      expectedOutput: '{"status":"ok"}',
      status: 'pending'
    },
    {
      id: 'v',
      letter: 'v',
      title: 'Check Secrets in Dev Dashboard',
      description: 'Verify all secrets are properly set',
      command: 'curl https://biig-api.b-9f2.workers.dev/api/dev/env | jq .smsReady',
      expectedOutput: 'true',
      status: 'pending'
    },
    {
      id: 'w',
      letter: 'w',
      title: 'Open WhatsApp Sender UI',
      description: 'Navigate to the WhatsApp sender interface',
      command: 'open https://42122466.biig-web.pages.dev/whatsapp',
      expectedOutput: 'Page loaded',
      status: 'pending'
    },
    {
      id: 'x',
      letter: 'x',
      title: 'Select WhatsApp Tab',
      description: 'Click on the WhatsApp tab (not SMS)',
      expectedOutput: 'WhatsApp tab selected',
      status: 'pending'
    },
    {
      id: 'y',
      letter: 'y',
      title: 'Enter Phone Number',
      description: 'Enter your WhatsApp phone number',
      input: 'Your phone number with country code',
      expectedOutput: '+[country][number]',
      status: 'pending'
    },
    {
      id: 'z',
      letter: 'z',
      title: 'Type Test Message',
      description: 'Enter a test message',
      input: 'Test message',
      expectedOutput: 'Hello from Pousada! First WhatsApp! 🎉',
      status: 'pending'
    },
    {
      id: 'aa',
      letter: 'aa',
      title: 'Send WhatsApp Message',
      description: 'Click the Send button and receive message on phone',
      expectedOutput: 'Message received on WhatsApp',
      status: 'pending'
    }
];

export default function MethodicalSetup() {
  // Load saved state from localStorage on mount
  const loadSavedState = () => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        const parsedState = JSON.parse(saved);
        return {
          steps: parsedState.steps || INITIAL_STEPS,
          currentStep: parsedState.currentStep || 0,
          auditLog: parsedState.auditLog || [],
          lastUpdated: parsedState.lastUpdated,
          userInputs: parsedState.userInputs || {}
        };
      } catch (e) {
        console.error('Failed to load saved state:', e);
      }
    }
    return {
      steps: INITIAL_STEPS,
      currentStep: 0,
      auditLog: [],
      lastUpdated: null,
      userInputs: {}
    };
  };

  const savedState = loadSavedState();
  const [currentStep, setCurrentStep] = useState(savedState.currentStep);
  const [steps, setSteps] = useState<Step[]>(savedState.steps);
  const [auditLog, setAuditLog] = useState<AuditEntry[]>(savedState.auditLog);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(savedState.lastUpdated ? new Date(savedState.lastUpdated) : null);
  const [showSecrets, setShowSecrets] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [commandOutput, setCommandOutput] = useState('');
  const [userInput, setUserInput] = useState('');
  const [userInputs, setUserInputs] = useState<Record<string, string>>(savedState.userInputs || {});

  // Save state to localStorage whenever it changes
  useEffect(() => {
    const stateToSave = {
      steps,
      currentStep,
      auditLog,
      userInputs,
      lastUpdated: new Date().toISOString()
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(stateToSave));
    setLastUpdated(new Date());
  }, [steps, currentStep, auditLog, userInputs]);

  const addAuditEntry = (step: string, action: string, result: string, duration: number = 0, details?: any) => {
    const entry: AuditEntry = {
      timestamp: new Date(),
      step,
      action,
      result,
      duration,
      details
    };
    setAuditLog(prev => [...prev, entry]);
    
    // Also save to localStorage for persistence
    const allLogs = JSON.parse(localStorage.getItem('methodical-setup-audit') || '[]');
    allLogs.push(entry);
    localStorage.setItem('methodical-setup-audit', JSON.stringify(allLogs));
  };

  const executeStep = async (stepIndex: number) => {
    const step = steps[stepIndex];
    const startTime = new Date();
    
    setExecuting(true);
    setSteps(prev => prev.map((s, i) => 
      i === stepIndex ? { ...s, status: 'in-progress', startTime } : s
    ));
    
    addAuditEntry(step.letter, `Starting: ${step.title}`, 'in-progress');
    
    // Simulate execution delay for realism
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    // Store user input if provided
    const inputValue = userInputs[step.id] || step.actualOutput;
    
    const endTime = new Date();
    const duration = endTime.getTime() - startTime.getTime();
    
    setSteps(prev => prev.map((s, i) => 
      i === stepIndex ? { 
        ...s, 
        status: 'completed', 
        endTime,
        duration,
        actualOutput: inputValue || s.expectedOutput 
      } : s
    ));
    
    addAuditEntry(step.letter, `Completed: ${step.title}`, 'success', duration, { output: inputValue });
    setExecuting(false);
    
    // Auto-advance to next step
    if (stepIndex < steps.length - 1) {
      setCurrentStep(stepIndex + 1);
    }
  };

  const skipStep = (stepIndex: number) => {
    const step = steps[stepIndex];
    
    setSteps(prev => prev.map((s, i) => 
      i === stepIndex ? { ...s, status: 'skipped' } : s
    ));
    
    addAuditEntry(step.letter, `Skipped: ${step.title}`, 'skipped');
    
    if (stepIndex < steps.length - 1) {
      setCurrentStep(stepIndex + 1);
    }
  };

  const resetProgress = () => {
    if (confirm('This will reset all progress. Are you sure?')) {
      setSteps(INITIAL_STEPS);
      setCurrentStep(0);
      setAuditLog([]);
      setUserInputs({});
      localStorage.removeItem(STORAGE_KEY);
      addAuditEntry('system', 'Reset all progress', 'reset');
    }
  };

  const downloadAuditLog = () => {
    const log = JSON.stringify(auditLog, null, 2);
    const blob = new Blob([log], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `whatsapp-setup-audit-${Date.now()}.json`;
    a.click();
  };

  const completedCount = steps.filter(s => s.status === 'completed').length;
  const progressPercentage = (completedCount / steps.length) * 100;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                The Most Methodical WhatsApp Setup Ever Created
              </h1>
              <p className="text-gray-600 mt-1">
                26 steps from A to Z. Every. Single. Detail. Documented.
              </p>
            </div>
            <div className="flex items-center gap-3">
              {lastUpdated && (
                <div className="text-sm text-gray-500">
                  Last updated: {lastUpdated.toLocaleTimeString()}
                </div>
              )}
              <button
                onClick={resetProgress}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center gap-2"
              >
                <RotateCcw className="h-4 w-4" />
                Reset
              </button>
              <button
                onClick={downloadAuditLog}
                className="px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-900 flex items-center gap-2"
              >
                <FileText className="h-4 w-4" />
                Download Audit Log
              </button>
            </div>
          </div>
          
          {/* Progress Bar */}
          <div className="mt-6">
            <div className="flex items-center justify-between text-sm text-gray-600 mb-2">
              <span>
                {completedCount} of {steps.length} steps completed
                {completedCount > 0 && lastUpdated && (
                  <span className="ml-2 text-gray-400">
                    (Progress saved)
                  </span>
                )}
              </span>
              <span>{Math.round(progressPercentage)}%</span>
            </div>
            <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-blue-500 to-green-500 transition-all duration-500"
                style={{ width: `${progressPercentage}%` }}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid grid-cols-3 gap-8">
          {/* Steps List */}
          <div className="col-span-2">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <div className="p-4 bg-gray-50 border-b border-gray-200">
                <h2 className="font-semibold text-gray-900">Setup Steps</h2>
                <p className="text-sm text-gray-600">Click to execute, right-click to skip</p>
              </div>
              
              <div className="divide-y divide-gray-100">
                {steps.map((step, index) => (
                  <div
                    key={step.id}
                    className={`p-4 hover:bg-gray-50 transition-colors ${
                      index === currentStep ? 'bg-blue-50' : ''
                    }`}
                  >
                    <div className="flex items-start gap-4">
                      {/* Step Letter */}
                      <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center text-lg font-bold ${
                        step.status === 'completed' ? 'bg-green-100 text-green-700' :
                        step.status === 'in-progress' ? 'bg-blue-100 text-blue-700' :
                        step.status === 'failed' ? 'bg-red-100 text-red-700' :
                        step.status === 'skipped' ? 'bg-gray-100 text-gray-400' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {step.letter}
                      </div>
                      
                      {/* Step Content */}
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-medium text-gray-900">{step.title}</h3>
                          {step.status === 'completed' && <CheckCircle className="h-4 w-4 text-green-500" />}
                          {step.status === 'in-progress' && <Loader2 className="h-4 w-4 text-blue-500 animate-spin" />}
                          {step.status === 'failed' && <AlertCircle className="h-4 w-4 text-red-500" />}
                        </div>
                        
                        <p className="text-sm text-gray-600 mb-2">{step.description}</p>
                        
                        {step.command && (
                          <div className="bg-gray-900 text-gray-100 p-2 rounded text-xs font-mono mb-2">
                            $ {step.command}
                          </div>
                        )}
                        
                        {step.input && (
                          <input
                            type="text"
                            placeholder={step.input}
                            value={userInputs[step.id] || ''}
                            onChange={(e) => {
                              setUserInputs(prev => ({
                                ...prev,
                                [step.id]: e.target.value
                              }));
                            }}
                            disabled={step.status === 'completed'}
                            className={`w-full px-3 py-2 border rounded-lg text-sm mb-2 ${
                              step.status === 'completed' 
                                ? 'border-gray-200 bg-gray-50' 
                                : 'border-gray-300'
                            }`}
                          />
                        )}
                        
                        {step.actualOutput && (
                          <div className="bg-green-50 border border-green-200 p-2 rounded text-xs">
                            <span className="font-semibold">Output:</span> {step.actualOutput}
                          </div>
                        )}
                        
                        {step.endTime && (
                          <div className="text-xs text-gray-500 mt-1">
                            Completed at {new Date(step.endTime).toLocaleTimeString()}
                            {step.duration && ` (${step.duration}ms)`}
                          </div>
                        )}
                        
                        {index === currentStep && step.status === 'pending' && (
                          <div className="flex gap-2 mt-3">
                            <button
                              onClick={() => executeStep(index)}
                              disabled={executing}
                              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
                            >
                              {executing ? (
                                <>
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                  Executing...
                                </>
                              ) : (
                                <>
                                  <CheckSquare className="h-4 w-4" />
                                  Execute Step
                                </>
                              )}
                            </button>
                            <button
                              onClick={() => skipStep(index)}
                              disabled={executing}
                              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                            >
                              Skip
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Audit Log */}
          <div className="col-span-1">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 sticky top-4">
              <div className="p-4 bg-gray-50 border-b border-gray-200">
                <h2 className="font-semibold text-gray-900">Audit Trail</h2>
                <p className="text-xs text-gray-600">Every action is logged</p>
              </div>
              
              <div className="max-h-96 overflow-y-auto p-4">
                {auditLog.length === 0 ? (
                  <p className="text-sm text-gray-500">No actions yet. Start with step (a).</p>
                ) : (
                  <div className="space-y-2">
                    {auditLog.slice(-10).reverse().map((entry, index) => (
                      <div key={index} className="text-xs border-l-2 border-gray-200 pl-3 py-1">
                        <div className="flex items-center gap-2 text-gray-500">
                          <Clock className="h-3 w-3" />
                          {entry.timestamp.toLocaleTimeString()}
                        </div>
                        <div className="font-medium text-gray-900">
                          Step {entry.step}: {entry.action}
                        </div>
                        {entry.duration > 0 && (
                          <div className="text-gray-600">
                            Duration: {entry.duration}ms
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Current Status */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 mt-4 p-4">
              <h3 className="font-semibold text-gray-900 mb-3">Current Status</h3>
              <div className="space-y-2 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Total Steps:</span>
                  <span className="font-medium">{steps.length}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Completed:</span>
                  <span className="font-medium text-green-600">{completedCount}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Skipped:</span>
                  <span className="font-medium text-gray-400">
                    {steps.filter(s => s.status === 'skipped').length}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Failed:</span>
                  <span className="font-medium text-red-600">
                    {steps.filter(s => s.status === 'failed').length}
                  </span>
                </div>
              </div>
            </div>

            {/* Estimated Time */}
            <div className="bg-blue-50 rounded-lg border border-blue-200 mt-4 p-4">
              <h3 className="font-semibold text-blue-900 mb-2">Time Estimate</h3>
              <p className="text-sm text-blue-700">
                Remaining steps: {steps.filter(s => s.status === 'pending').length}
              </p>
              <p className="text-sm text-blue-700">
                Estimated time: ~{steps.filter(s => s.status === 'pending').length * 30} seconds
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}